/**
 * External dependencies.
 */
import type { GetStaticProps, GetStaticPaths, NextPage } from "next";

/**
 * Internal dependencies.
 */
import { fetchAPI, getLocale, getLocalizationData, getSlug, loadTranslation } from "utils";
import useBlogTagSlug from "hooks/use-blog-tag-slug";
import BlogLogic from "components/blog-logic/blog-logic";

interface BlogPostTagProps {
  data: any;
  articleTags: any;
  articles: any;
  locales: any;
  locale: string;
}

const BlogPostTag: NextPage<BlogPostTagProps> = (prop) => {
  const articleTags = prop.articleTags?.data ?? [];
  const hreflangs = useBlogTagSlug(articleTags);

  return (
    <BlogLogic
      hreflangs={hreflangs}
      prop={prop}
    />
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const blog = fetchAPI("/blog", {
    populate: "*",
    locale,
  });

  const articleTags = fetchAPI("/article-tags", {
    populate: "*",
    locale,
  });

  const articles = fetchAPI("/articles", {
    populate: {
      SEO: {
        populate: "*",
      },
      Image: "*",
      localizations: "*",
    },
    locale,
    pagination: {
      page: 1,
      pageSize: 18,
    },
    filters: {
      article_tags: {
        slug: ctx.params?.slug,
      },
    },
  });

  const responses = await Promise.all([blog, articleTags, articles]);
  const currentSlug = ctx?.params?.slug || null;
  const currentTag = responses[1].data.find(({attributes}:any) => attributes.slug_overwrite === currentSlug || attributes.slug === currentSlug);

  return {
    props: {
      translation,
      data: responses[0],
      articleTags: responses[1],
      articles: responses[2],
      locale: ctx.locale,
      localizationData: getLocalizationData(currentTag),
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths: any = [];

  const { data } = await fetchAPI("/article-tags", {
    populate: "*",
  });

  data?.map(({ attributes }: any) => {
    if (attributes.localizations.data.length) {
      attributes.localizations.data.map((locale: any) => {
        paths.push({
          params: { slug: getSlug(locale.attributes) },
          locale: locale.attributes.locale.toLowerCase(),
        });
      });
    }

    paths.push({
      params: { slug: getSlug(attributes) },
      locale: attributes.locale?.toLowerCase(),
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export default BlogPostTag;
